<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const FORM_CONFIG = {
  FIRST_NAME:    { field: 'firstName',                          label: 'Nome',           required: true  },
  LAST_NAME:     { field: 'lastName',                           label: 'Sobrenome',      required: false },
  EMAIL_ADDRESS: { field: 'email',                              label: 'Email',          required: false },
  PHONE_NUMBER:  { field: 'phoneNumber',                        label: 'Número',         required: true  },
  CITY:          { field: 'additionalAttributes.city',          label: 'Nome da cidade', required: false },
  COUNTRY:       { field: 'additionalAttributes.countryCode',   label: 'Selecione o país', required: false },
  BIO:           { field: 'additionalAttributes.description',   label: 'Bio',            required: false },
  COMPANY_NAME:  { field: 'additionalAttributes.companyName',   label: 'Empresa',        required: false },
};

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive({ ...defaultState });

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

// SZ: collapsable "Redes sociais" (Figma #1652706304)
const socialOpen = ref(false);

const isFormInvalid = computed(() => v$.value.$invalid);

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    additionalAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  Object.assign(state, {
    id,
    name,
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      socialProfiles,
    },
  });
};

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const editDetailsForm = computed(() =>
  Object.keys(FORM_CONFIG).map(key => ({
    key,
    label: FORM_CONFIG[key].label,
    required: !!FORM_CONFIG[key].required,
    placeholder: t(
      `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`
    ),
  }))
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      const isFormValid = await v$.value.$validate();
      if (isFormValid) {
        const { firstName, lastName, ...stateWithoutNames } = state;
        emit('update', stateWithoutNames);
      }
    },
  });
};

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', state);
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, defaultState);
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="sz-cdf-form">
    <!-- Grid 2-col com os campos do contato — SZ Figma #1652706300.
         Cada item é wrappeado com label próprio acima do input. -->
    <div class="sz-cdf-grid">
      <div
        v-for="item in editDetailsForm"
        :key="item.key"
        class="sz-cdf-field"
      >
        <label class="sz-cdf-label">
          <span v-if="item.required" class="sz-cdf-required" aria-hidden="true">*</span>
          {{ item.label }}
        </label>
        <ComboBox
          v-if="item.key === 'COUNTRY'"
          v-model="state.additionalAttributes.countryCode"
          :options="countryOptions"
          :placeholder="item.placeholder"
          class="[&>div>button]:h-10"
          :class="{
            '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
              !isDetailsView,
            '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
          }"
          @update:model-value="handleCountrySelection"
        />
        <PhoneNumberInput
          v-else-if="item.key === 'PHONE_NUMBER'"
          v-model="getFormBinding(item.key).value"
          :placeholder="item.placeholder"
          :show-border="isDetailsView"
        />
        <Input
          v-else
          v-model="getFormBinding(item.key).value"
          :placeholder="item.placeholder"
          :message-type="getMessageType(item.key)"
          :custom-input-class="`h-10 !pt-1 !pb-1 ${
            !isDetailsView
              ? '[&:not(.error,.focus)]:!outline-transparent'
              : ''
          }`"
          class="w-full"
          @input="
            isValidationField(item.key) &&
              v$[getValidationKey(item.key)].$touch()
          "
          @blur="
            isValidationField(item.key) &&
              v$[getValidationKey(item.key)].$touch()
          "
        />
      </div>
    </div>

    <!-- Card colapsável "Redes sociais" — SZ Figma #1652706304 -->
    <div class="sz-cdf-social-card">
      <button
        type="button"
        class="sz-cdf-social-toggle"
        :aria-expanded="socialOpen"
        @click="socialOpen = !socialOpen"
      >
        <span class="sz-cdf-social-title">{{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}</span>
        <svg
          width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" stroke-width="1.5"
          stroke-linecap="round" stroke-linejoin="round"
          class="sz-cdf-social-chevron"
          :class="{ 'sz-cdf-social-chevron--open': socialOpen }"
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      <div v-if="socialOpen" class="sz-cdf-social-body">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="sz-cdf-social-pill"
        >
          <Icon :icon="item.icon" class="flex-shrink-0 text-n-slate-11 size-4" />
          <input
            v-model="state.additionalAttributes.socialProfiles[item.key.toLowerCase()]"
            class="sz-cdf-social-input"
            :placeholder="item.placeholder"
            @input="emit('update', state)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* SZ Figma — form do detalhe do contato */
.sz-cdf-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  font-family: 'Inter', system-ui, sans-serif;
}
.sz-cdf-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 640px) {
  .sz-cdf-grid { grid-template-columns: 1fr; }
}

/* SZ Figma — overrides do <Input> pra bater exatamente com o spec:
   - Field: bg #FFFFFF, border 1px #D1D5DB, radius 8px, padding 8px 16px, height 40px
   - Texto: Inter 400 16px/24px color #000
*/

/* Field container — agrupa label + input com gap 8px */
.sz-cdf-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}
/* Label exato do Figma: Inter 500 14/20 #000, asterisk vermelho ANTES */
.sz-cdf-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: inherit;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
}
.dark .sz-cdf-label { color: #FFFFFF; }
.sz-cdf-required {
  font-family: inherit;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.32;
  color: #DC2626;
}

/* Esconde labels internas que o <Input>/<ComboBox> renderiza
   (passaríamos vazio mas o componente decide via prop) — como não passamos
   :label, eles não renderizam, então essa regra é só guard. */
.sz-cdf-field > .sz-cdf-label + :deep(label) {
  display: none !important;
}
/* Exclui type=search (input de busca do dropdown de países) — antes o
   padding 8 16 sobrescrevia o pl-10 e a lupa ficava sobre o texto. */
.sz-cdf-grid :deep(input):not([type="checkbox"]):not([type="radio"]):not([type="search"]):not([type="tel"]),
.sz-cdf-grid :deep(textarea) {
  height: 40px;
  padding: 8px 16px !important;
  background: #FFFFFF !important;
  border: 1px solid #D1D5DB !important;
  border-radius: 8px !important;
  outline: none !important;
  font-family: inherit;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #000000;
  box-sizing: border-box;
}
/* Centraliza verticalmente a lupa do search do dropdown */
.sz-cdf-grid :deep(.relative.flex.items-center span.absolute.i-lucide-search) {
  top: 50% !important;
  transform: translateY(-50%) !important;
}
.sz-cdf-grid :deep(input):focus {
  border-color: #388E3C !important;
}
/* Dark precisa MESMA specificity (5+ :not) da regra clara pra sobrescrever */
.dark .sz-cdf-grid :deep(input):not([type="checkbox"]):not([type="radio"]):not([type="search"]):not([type="tel"]),
.dark .sz-cdf-grid :deep(textarea) {
  background: #242528 !important;
  border-color: #303138 !important;
  color: #FFFFFF !important;
}
.dark .sz-cdf-grid :deep(input):not([type="checkbox"]):not([type="radio"]):not([type="search"]):not([type="tel"])::placeholder,
.dark .sz-cdf-grid :deep(textarea)::placeholder {
  color: #92939A !important;
}

/* ComboBox (país) — alinhar com o resto */
.sz-cdf-grid :deep(button[role="combobox"]),
.sz-cdf-grid :deep(div[role="combobox"] > button),
.sz-cdf-grid :deep([class*="combobox"] button),
.sz-cdf-grid :deep([class*="combo-box"] button) {
  min-height: 40px !important;
  height: 40px !important;
  padding: 8px 16px !important;
  background: #FFFFFF !important;
  border: 1px solid #D1D5DB !important;
  border-radius: 8px !important;
  font-family: inherit !important;
  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 24px !important;
  color: #000000 !important;
}
.dark .sz-cdf-grid :deep(button[role="combobox"]),
.dark .sz-cdf-grid :deep(div[role="combobox"] > button),
.dark .sz-cdf-grid :deep([class*="combobox"] button),
.dark .sz-cdf-grid :deep([class*="combo-box"] button) {
  background: #1D1E21 !important;
  border-color: #3D3F43 !important;
  color: #FFFFFF !important;
}

/* PhoneNumberInput — recriado exato conforme Figma:
   - Wrapper: height 48px, bg #FFFFFF (dark #242528), border 1px #D1D5DB (dark #303138), radius 8px
   - Bandeira + chevron à esquerda (sem texto "BR"), padding 12px
   - Input à direita, placeholder/value 16px/24px */
.sz-cdf-grid :deep(.relative.flex.items-center) {
  height: 48px !important;
  background: #FFFFFF !important;
  outline: none !important;
  border: 1px solid #D1D5DB !important;
  border-radius: 8px !important;
  padding: 0 !important;
}
/* Dropdown do seletor de país — escapa do wrapper (não corta) */
.sz-cdf-grid :deep(.relative.flex.items-center > div[class*="z-[100]"]),
.sz-cdf-grid :deep(.relative.flex.items-center > .absolute) {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  margin-top: 4px !important;
  max-height: 240px !important;
  background: #FFFFFF !important;
  border: 1px solid #D1D5DB !important;
  border-radius: 8px !important;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.12) !important;
  z-index: 100 !important;
}
.dark .sz-cdf-grid :deep(.relative.flex.items-center > div[class*="z-[100]"]),
.dark .sz-cdf-grid :deep(.relative.flex.items-center > .absolute) {
  background: #1D1E21 !important;
  border-color: #3D3F43 !important;
}
/* Targeting input[type="tel"] específico pra NÃO atingir o input de busca
   do dropdown de países (type=search) — antes esmagava o pl-10 da lupa. */
.sz-cdf-grid :deep(.relative.flex.items-center) input[type="tel"] {
  height: 46px !important;
  padding: 12px !important;
  font-size: 16px !important;
  line-height: 24px !important;
  border: 0 !important;
  outline: none !important;
  background: transparent !important;
  color: #000000;
}
.sz-cdf-grid :deep(.relative.flex.items-center) input[type="tel"]::placeholder {
  color: #92939A;
}
/* Botão da bandeira/chevron — altura 48, padding 12, sem o texto "BR".
   O `activeCountry.emoji` (🇧🇷) continua visível + chevron. */
.sz-cdf-grid :deep(.relative.flex.items-center button[class*="rounded-r-none"]) {
  height: 48px !important;
  padding: 12px !important;
  background: transparent !important;
  border-right: 1px solid #D1D5DB !important;
  border-radius: 8px 0 0 8px !important;
  top: 0 !important;
  margin: 0 !important;
  gap: 4px !important;
}
.dark .sz-cdf-grid :deep(.relative.flex.items-center) {
  background: #242528 !important;
  border-color: #303138 !important;
}
.dark .sz-cdf-grid :deep(.relative.flex.items-center) input[type="tel"] {
  color: #FFFFFF !important;
}
.dark .sz-cdf-grid :deep(.relative.flex.items-center) input[type="tel"]::placeholder {
  color: #92939A !important;
}
.dark .sz-cdf-grid :deep(.relative.flex.items-center button[class*="rounded-r-none"]) {
  border-right-color: #303138 !important;
  color: #FFFFFF !important;
}

/* Card "Redes sociais" — colapsável (Figma #1652706304) */
.sz-cdf-social-card {
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #E6E6E6;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.1);
  overflow: hidden;
}
.dark .sz-cdf-social-card {
  background: #1D1E21;
  border-color: #2A2C30;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}
.sz-cdf-social-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: inherit;
}
.sz-cdf-social-title {
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #000000;
}
.dark .sz-cdf-social-title { color: #FFFFFF; }
.sz-cdf-social-chevron {
  color: #000000;
  transition: transform 0.15s ease;
}
.sz-cdf-social-chevron--open { transform: rotate(180deg); }
.dark .sz-cdf-social-chevron { color: #FFFFFF; }
.sz-cdf-social-body {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 16px 16px;
}
.sz-cdf-social-pill {
  display: flex;
  align-items: center;
  height: 32px;
  gap: 8px;
  padding: 0 8px;
  border-radius: 8px;
  background: #F5F5F5;
}
.dark .sz-cdf-social-pill { background: #2A2C30; }
.sz-cdf-social-input {
  background: transparent;
  border: 0;
  outline: 0;
  font-size: 14px;
  color: #1F2937;
  min-width: 120px;
}
.sz-cdf-social-input::placeholder { color: #9CA3AF; }
.dark .sz-cdf-social-input { color: #E5E7EB; }
.dark .sz-cdf-social-input::placeholder { color: #9797A0; }
</style>
